import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsPanel, TabsTab } from "@/components/ui/tabs";
import { NAMESPACES } from "@/i18n/constants";
import { AvailableTab } from "./available-tab";
import { DisplayedTab } from "./displayed-tab";
import { withoutPlotted } from "./filter-picker-streams";
import type { PickerFilter } from "./picker-filter";
import type { PickerStream } from "./picker-stream";
import type { CatalogStatus } from "./use-picker-catalog";

export const STREAM_PICKER_HEIGHT_PX = 470;

export type PickerTab = "available" | "displayed";

export function StreamPickerPanel({
    plotted,
    addedIds,
    catalog,
    status,
    onAdd,
    onRemove,
    filter,
    onFilterChange,
    isAdmin,
    initialTab = "available",
    initialQuery,
}: {
    plotted: readonly PickerStream[];
    addedIds: ReadonlySet<number>;
    catalog: readonly PickerStream[];
    status: CatalogStatus;
    onAdd: (streamId: number) => void;
    onRemove: (streamId: number) => void;
    filter: PickerFilter;
    onFilterChange: (filter: PickerFilter) => void;
    isAdmin: boolean;
    initialTab?: PickerTab;
    initialQuery?: string;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    const [tab, setTab] = useState<PickerTab>(initialTab);
    const unplotted = useMemo(() => withoutPlotted(catalog, plotted), [catalog, plotted]);
    const availableCount = unplotted.filter((stream) => filter[stream.visibility]).length;

    return (
        // No --available-height cap: in min() it breaks Base UI's measuring, in max-h it stops the flip.
        <Tabs
            className={"max-h-full w-[360px] max-w-full gap-0"}
            onValueChange={(value) => setTab(value as PickerTab)}
            style={{ height: STREAM_PICKER_HEIGHT_PX }}
            value={tab}
        >
            <TabsList className={"w-full border-b px-2.5"} variant={"underline"}>
                <TabsTab value={"available"}>
                    {t("available-tab")}
                    {status === "ready" && <TabCount count={availableCount} />}
                </TabsTab>
                <TabsTab value={"displayed"}>
                    {t("displayed-tab")}
                    <TabCount count={plotted.length} />
                </TabsTab>
            </TabsList>
            <TabsPanel className={"flex min-h-0 flex-col"} value={"available"}>
                <AvailableTab
                    unplotted={unplotted}
                    status={status}
                    onAdd={onAdd}
                    filter={filter}
                    onFilterChange={onFilterChange}
                    isAdmin={isAdmin}
                    initialQuery={initialQuery}
                />
            </TabsPanel>
            <TabsPanel className={"min-h-0 overflow-y-auto"} value={"displayed"}>
                <DisplayedTab plotted={plotted} addedIds={addedIds} onRemove={onRemove} />
            </TabsPanel>
        </Tabs>
    );
}

function TabCount({ count }: { count: number }) {
    return (
        <Badge className={"min-w-5 rounded-full px-1.5 tabular-nums"} size={"sm"} variant={"secondary"}>
            {count}
        </Badge>
    );
}
